import React, { useCallback, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import * as DropdownMenu from "@radix-ui/react-dropdown-menu";
import { getUser } from "selectors/commonSelectors";
import { User } from "../../features/user";
import { hasEntitlement } from "../../utils/has-entitlement";
import { cn } from "@/lib/utils";
import {
  HEADER_SUPPORT_CLICK,
  SERVICE_STATUS_CLICK,
} from "../../constants/Events";
import { useTrackEvent } from "../../libs/analytics";
import {
  ServiceStatusDot,
  STATUS_PAGE_URL,
  useServiceStatus,
} from "../../features/service-status";
import { openHubSpotSupportWidget } from "../../libs/support";
import {
  HUBSPOT_INLINE_CHAT_CONTAINER_ID,
  SUPPORT_CHAT_OPEN_EVENT,
} from "../../libs/support/hubspot-inline-chat";
import type { SupportChatOpenDetail } from "../../libs/support/hubspot-inline-chat";
import type { HubSpotHelpChatTopic } from "../../libs/support/open-support-chat";
import { Z_INDEX_MODAL_BACKDROP } from "../../lib/modal-stack";
import { topNavGhostIconButtonClassName } from "./top-nav-button";

const KNOWLEDGE_BASE_URL = "https://help.skedsocial.com/";
const SUPPORT_EMAIL = "hello@skedsocial.com";
const BOOK_A_CALL_URL =
  "https://www2.skedsocial.com/meetings/rich-henson/account-connection";
const supportMenuItemClassName =
  "flex cursor-pointer items-center gap-2.5 rounded-md px-3 py-2.5 text-sm text-neutral-800 outline-none transition-colors hover:bg-blue-50 hover:text-blue-700 focus:bg-blue-50 focus:text-blue-700";
const supportMenuIconClassName = "w-4 text-center";

export function HeaderSupportMenu({
  calendarUplift = false,
}: {
  calendarUplift?: boolean;
}): React.ReactElement {
  const user: User = useSelector(getUser);
  const trackEvent = useTrackEvent();
  const [chatPanelOpen, setChatPanelOpen] = useState(false);
  const canBookCall = hasEntitlement(user, "support-book-call");
  const serviceStatus = useServiceStatus();

  const openChatPanel = useCallback(
    (
      message = "",
      helpChatTopic: HubSpotHelpChatTopic = "generic",
      source = "header"
    ) => {
      setChatPanelOpen(true);
      trackEvent({
        eventName: HEADER_SUPPORT_CLICK,
        metadata: { action: "live_chat", source },
        useServices: ["AMP", "HS"],
      });
      openHubSpotSupportWidget(message, helpChatTopic);
    },
    [trackEvent]
  );

  useEffect(() => {
    const handleOpenSupportChat = (event: Event) => {
      const detail = (event as CustomEvent<SupportChatOpenDetail>).detail;
      openChatPanel(
        detail?.message || "",
        (detail?.helpChatTopic as HubSpotHelpChatTopic | undefined) ??
          "generic",
        "support_helper"
      );
    };

    window.addEventListener(SUPPORT_CHAT_OPEN_EVENT, handleOpenSupportChat);
    return () => {
      window.removeEventListener(
        SUPPORT_CHAT_OPEN_EVENT,
        handleOpenSupportChat
      );
    };
  }, [openChatPanel]);

  const trackStatusClick = (source: string) => {
    trackEvent({
      eventName: SERVICE_STATUS_CLICK,
      metadata: { source, state: serviceStatus.overall },
      useServices: ["AMP", "HS"],
    });
  };

  const trackSupportAction = (action: string) => {
    trackEvent({
      eventName: HEADER_SUPPORT_CLICK,
      metadata: { action },
      useServices: ["AMP", "HS"],
    });
  };

  return (
    <>
      <DropdownMenu.Root modal={false}>
        <DropdownMenu.Trigger asChild>
          <button
            type="button"
            className={cn(
              topNavGhostIconButtonClassName,
              calendarUplift && "text-[#333333]",
              "data-[state=open]:bg-blue-50 data-[state=open]:text-blue-500"
            )}
            data-testid="support-menu-trigger"
            aria-label="Support menu"
          >
            <i className="fa-regular fa-headset text-base" />
            <ServiceStatusDot
              tone={serviceStatus.dot}
              className="absolute right-0.5 top-0.5 h-2 w-2"
            />
          </button>
        </DropdownMenu.Trigger>
        <DropdownMenu.Portal>
          <DropdownMenu.Content
            className="min-w-[190px] overflow-hidden rounded-lg bg-white p-1.5 shadow-lg border border-neutral-100 data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=open]:zoom-in-95 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95 data-[state=closed]:fill-mode-forwards duration-200"
            data-testid="support-menu-links"
            align="end"
            sideOffset={8}
            style={{ zIndex: Z_INDEX_MODAL_BACKDROP - 1 }}
          >
            {/* Plain link out (new tab), like the sibling support links — an
                anchored popover here fought the dropdown's own dismiss and
                vanished (#4678). The dot carries state; on feed error it renders
                nothing (never a false green) and we show the link glyph. */}
            <DropdownMenu.Item asChild>
              <a
                href={STATUS_PAGE_URL}
                target="_blank"
                rel="noopener noreferrer"
                id="dashboard-support-system-status"
                className={supportMenuItemClassName}
                data-testid="support-menu-system-status"
                onClick={() => trackStatusClick("menu_link")}
              >
                {serviceStatus.dot === "none" ? (
                  <i
                    className={cn(
                      "fa-regular fa-arrow-up-right-from-square",
                      supportMenuIconClassName
                    )}
                  />
                ) : (
                  <span
                    className={cn(
                      "flex items-center justify-center",
                      supportMenuIconClassName
                    )}
                  >
                    <ServiceStatusDot tone={serviceStatus.dot} />
                  </span>
                )}
                System status
                {serviceStatus.label ? ` · ${serviceStatus.label}` : ""}
              </a>
            </DropdownMenu.Item>

            <DropdownMenu.Item
              id="dashboard-support-live-chat"
              className={supportMenuItemClassName}
              onSelect={() => openChatPanel()}
            >
              <i
                className={cn(
                  "fa-regular fa-message-heart",
                  supportMenuIconClassName
                )}
              />
              Live chat
            </DropdownMenu.Item>

            <DropdownMenu.Item asChild>
              <a
                href={KNOWLEDGE_BASE_URL}
                target="_blank"
                rel="noopener noreferrer"
                id="dashboard-support-knowledge-base"
                className={supportMenuItemClassName}
                onClick={() => trackSupportAction("knowledge_base")}
              >
                <i
                  className={cn(
                    "fa-regular fa-file-circle-question",
                    supportMenuIconClassName
                  )}
                />
                Knowledge base
              </a>
            </DropdownMenu.Item>

            <DropdownMenu.Item asChild>
              <a
                href={`mailto:${SUPPORT_EMAIL}`}
                id="dashboard-support-email"
                className={supportMenuItemClassName}
                onClick={() => trackSupportAction("email")}
              >
                <i
                  className={cn(
                    "fa-regular fa-envelope",
                    supportMenuIconClassName
                  )}
                />
                Email
              </a>
            </DropdownMenu.Item>

            {canBookCall && (
              <DropdownMenu.Item asChild>
                <a
                  href={BOOK_A_CALL_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  id="dashboard-support-book-call"
                  className={supportMenuItemClassName}
                  onClick={() => trackSupportAction("book_call")}
                >
                  <i
                    className={cn(
                      "fa-regular fa-calendar-circle-user",
                      supportMenuIconClassName
                    )}
                  />
                  Book a call
                </a>
              </DropdownMenu.Item>
            )}
          </DropdownMenu.Content>
        </DropdownMenu.Portal>
      </DropdownMenu.Root>

      <aside
        className={cn(
          "fixed right-4 w-[333px] overflow-hidden rounded-t-lg rounded-b-[20px] border border-[rgba(26,24,35,0.08)] bg-white shadow-[0_1px_2px_rgba(16,24,40,0.05),0_12px_32px_rgba(16,24,40,0.12)] transition-[opacity,transform,visibility] duration-150 ease-in-out",
          chatPanelOpen
            ? "visible translate-x-0 opacity-100 pointer-events-auto"
            : "invisible translate-x-4 opacity-0 pointer-events-none"
        )}
        style={{
          height: "min(618px, calc(100vh - var(--header-height) - 24px))",
          top: "calc(var(--header-height) + 10px)",
          zIndex: Z_INDEX_MODAL_BACKDROP - 2,
        }}
        aria-hidden={!chatPanelOpen}
        data-testid="support-chat-panel"
      >
        <div className="flex h-12 items-center justify-between bg-[#4f31ff] px-3 text-sm font-semibold text-white">
          <span>Sked Support</span>
          <button
            type="button"
            className="flex h-7 w-7 cursor-pointer items-center justify-center rounded-md border-0 bg-transparent p-0 text-white hover:bg-white/15 focus-visible:bg-white/15 focus-visible:outline-none"
            onClick={() => setChatPanelOpen(false)}
            aria-label="Close support chat"
          >
            <i className="fa-regular fa-xmark" />
          </button>
        </div>
        <div
          id={HUBSPOT_INLINE_CHAT_CONTAINER_ID}
          className="h-[calc(100%-48px)] w-full"
        />
      </aside>
    </>
  );
}
